import React, { useCallback, useEffect, useState } from 'react';

import BirdSurveyIcon from 'img/icon/bird-survey.svg';

import Intro from 'components/Intro';
import Tab from 'components/Tab';
import LeafletMap from 'components/EcoEnvironmentalSurvey/LeafletMap/Content';
import {
  BIRD_SURVEY_COLUMNS,
  DEFAULT_BIRD_SURVEY_FILTERS,
  PAGE_SIZE_OPTIONS,
} from 'data/bird_survey_config';
import { API_URL } from 'utils/config';

const BIRD_SURVEY_API_URL = `${API_URL}/getBirdSurveys/`;
const BIRD_SURVEY_MAP_API_URL = `${API_URL}/getBirdSurveyMap/`;

const Content = () => {
  const [filters, setFilters] = useState(DEFAULT_BIRD_SURVEY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(
    DEFAULT_BIRD_SURVEY_FILTERS
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [birdSurveyRecords, setBirdSurveyRecords] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [nextPageUrl, setNextPageUrl] = useState('');
  const [pageUrls, setPageUrls] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [birdSurveyMapData, setBirdSurveyMapData] = useState(null);
  const [isMapLoading, setIsMapLoading] = useState(false);
  const [mapErrorMessage, setMapErrorMessage] = useState('');
  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
  const hasPrevPage = currentPage > 1 && !isLoading;
  const hasNextPage =
    currentPage < totalPages && !isLoading && Boolean(nextPageUrl);

  const pageData = {
    title: '鳥類調查',
    icon: BirdSurveyIcon,
    page: 'bird-survey',
    tabs: [
      {
        id: 1,
        title: '資料來源',
        content: (
          <>
            彰化縣野鳥學會提供本站固定調查資料同時也有上傳至相關生物資料庫，為提供完整資訊，介接{' '}
            <a
              href="https://tbiadata.tw/zh-hant/search/occurrence?record_type=occ&taxonGroup=%E9%B3%A5%E9%A1%9E&county=%E5%BD%B0%E5%8C%96%E7%B8%A3&municipality=%E8%8A%B3%E8%8B%91%E9%84%89&page=1&from=search&limit=10"
              className="e-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              台灣生物多樣性資訊聯盟(TBIA)
            </a>{' '}
            彰化縣芳苑鄉鳥類物種出現紀錄公開資料供查詢。
          </>
        ),
      },
    ],
    url: 'birdSurvey',
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
  };

  const buildBirdSurveyUrl = useCallback(
    (queryFilters = appliedFilters) => {
      const params = new URLSearchParams({
        limit: String(pageSize),
      });

      if (queryFilters.scientificName.trim()) {
        params.set('name', queryFilters.scientificName.trim());
      }
      if (queryFilters.taxonRank) {
        params.set('taxonRank', queryFilters.taxonRank);
      }
      if (queryFilters.startDate || queryFilters.endDate) {
        params.set(
          'eventDate',
          `${queryFilters.startDate || ''},${queryFilters.endDate || ''}`
        );
      }

      return `${BIRD_SURVEY_API_URL}?${params.toString()}`;
    },
    [appliedFilters, pageSize]
  );

  const fetchBirdSurvey = useCallback(
    async (url, page, shouldReset = false) => {
      setIsLoading(true);
      setErrorMessage('');

      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`鳥類調查 API 回應失敗：${response.status}`);
        }

        const result = await response.json();
        if (result.error) {
          throw new Error(result.error);
        }
        if (result.status?.code && result.status.code !== 200) {
          throw new Error(result.status.message || '鳥類調查 API 回應失敗');
        }

        const records = result.data || [];
        const nextUrl = result.links?.next || '';

        setBirdSurveyRecords(records);
        setTotalRecords(result.meta?.total || 0);
        setNextPageUrl(nextUrl);
        setCurrentPage(page);
        setPageUrls((prevUrls) => {
          const nextUrls = shouldReset
            ? { 1: url }
            : { ...prevUrls, [page]: url };
          if (nextUrl) {
            nextUrls[page + 1] = nextUrl;
          }
          return nextUrls;
        });
      } catch (error) {
        setBirdSurveyRecords([]);
        setTotalRecords(0);
        setNextPageUrl('');
        setErrorMessage(error.message || '鳥類調查資料載入失敗');
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const fetchBirdSurveyMap = useCallback(async () => {
    setIsMapLoading(true);
    setMapErrorMessage('');

    try {
      const response = await fetch(BIRD_SURVEY_MAP_API_URL);
      if (!response.ok) {
        throw new Error(`鳥類調查地圖 API 回應失敗：${response.status}`);
      }

      const result = await response.json();
      if (result.error) {
        throw new Error(result.error);
      }

      const geojson = result.data;
      if (geojson?.type !== 'FeatureCollection' || !geojson.features) {
        throw new Error('鳥類調查地圖 API 回傳格式錯誤');
      }

      setBirdSurveyMapData(geojson);
    } catch (error) {
      setBirdSurveyMapData(null);
      setMapErrorMessage(error.message || '鳥類調查地圖資料載入失敗');
    } finally {
      setIsMapLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBirdSurvey(buildBirdSurveyUrl(appliedFilters), 1, true);
  }, [appliedFilters, buildBirdSurveyUrl, fetchBirdSurvey]);

  useEffect(() => {
    fetchBirdSurveyMap();
  }, [fetchBirdSurveyMap]);

  const handleSearch = () => {
    setAppliedFilters({ ...filters });
  };

  const handleClearFilters = () => {
    setFilters(DEFAULT_BIRD_SURVEY_FILTERS);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
  };

  const handlePrevPage = () => {
    if (hasPrevPage) {
      fetchBirdSurvey(pageUrls[currentPage - 1], currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (hasNextPage) {
      fetchBirdSurvey(nextPageUrl, currentPage + 1);
    }
  };

  const formatTableValue = (record, column) => {
    const rawValue = record[column.key] ?? record[column.fallbackKey];

    if (typeof rawValue === 'boolean') {
      return rawValue ? '是' : '否';
    }
    if (rawValue === null || rawValue === undefined || rawValue === '') {
      return '-';
    }

    return rawValue;
  };

  return (
    <>
      <article className="l-eco-environmental-survey l-bird-survey">
        <section className="l-eco-environmental-survey__title u-section">
          <Intro data={pageData} />
          <Tab data={pageData} />
        </section>
        <section className="l-bird-survey__dashboard u-section">
          <div className="l-bird-survey__top">
            <div className="c-card l-bird-survey__filters">
              {/* <h2 className="c-title l-bird-survey__title">篩選條件</h2> */}
              <div className="l-bird-survey__filter-grid">
                <div className="l-bird-survey__field">
                  <label htmlFor="bird-scientific-name" className="form-label">
                    學名/中文名/中文別名/同物異名/誤用名
                  </label>
                  <input
                    id="bird-scientific-name"
                    name="scientificName"
                    type="text"
                    className="form-control"
                    value={filters.scientificName}
                    onChange={handleFilterChange}
                    placeholder="輸入關鍵字"
                  />
                </div>
                <div className="l-bird-survey__field">
                  <label htmlFor="bird-taxon-rank" className="form-label">
                    鑑定層級
                  </label>
                  <select
                    id="bird-taxon-rank"
                    name="taxonRank"
                    className="form-select"
                    value={filters.taxonRank}
                    onChange={handleFilterChange}
                  >
                    <option value="">全部</option>
                    <option value="kingdom">界</option>
                    <option value="phylum">門</option>
                    <option value="class">綱</option>
                    <option value="order">目</option>
                    <option value="family">科</option>
                    <option value="genus">屬</option>
                    <option value="species">種</option>
                    <option value="infraspecies">種下</option>
                  </select>
                </div>
                <div className="l-bird-survey__field">
                  <label htmlFor="bird-start-date" className="form-label">
                    調查起始日期
                  </label>
                  <input
                    id="bird-start-date"
                    name="startDate"
                    type="date"
                    className="form-control"
                    value={filters.startDate}
                    onChange={handleFilterChange}
                  />
                </div>
                <div className="l-bird-survey__field">
                  <label htmlFor="bird-end-date" className="form-label">
                    調查結束日期
                  </label>
                  <input
                    id="bird-end-date"
                    name="endDate"
                    type="date"
                    className="form-control"
                    value={filters.endDate}
                    onChange={handleFilterChange}
                  />
                </div>
              </div>
              <div className="l-bird-survey__actions">
                <button
                  type="button"
                  className="e-btn e-btn--muted"
                  onClick={handleClearFilters}
                >
                  清除所有選項
                </button>
                <button
                  type="button"
                  className="e-btn e-btn--primary"
                  onClick={handleSearch}
                >
                  搜尋
                </button>
              </div>
            </div>
            <div className="l-bird-survey__map">
              <LeafletMap birdSurveyMapData={birdSurveyMapData} />
              {isMapLoading && (
                <div className="l-bird-survey__map-message">
                  地圖資料載入中...
                </div>
              )}
              {mapErrorMessage && (
                <div className="l-bird-survey__map-message">
                  {mapErrorMessage}
                </div>
              )}
            </div>
          </div>
          {/* <div className="l-bird-survey__count-grid" aria-label="資料筆數">
            <div className="c-card l-bird-survey__count-card">
              <span className="l-bird-survey__count-label">目前資料筆數</span>
              <strong className="l-bird-survey__count-value">0</strong>
            </div>
          </div> */}
          <div className="c-card l-bird-survey__table-card">
            {/* <h2 className="c-title l-bird-survey__title">調查資料</h2> */}
            <div className="l-bird-survey__table-toolbar">
              <div className="l-bird-survey__table-count">
                搜尋結果共計 <strong>{totalRecords}</strong> 筆
              </div>
              <div className="l-bird-survey__pagination">
                <label
                  htmlFor="bird-survey-page-size"
                  className="l-bird-survey__page-size-label"
                >
                  每頁
                </label>
                <select
                  id="bird-survey-page-size"
                  className="form-select l-bird-survey__page-size"
                  value={pageSize}
                  onChange={handlePageSizeChange}
                >
                  {PAGE_SIZE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option} 筆
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className={`e-btn e-btn--muted ${
                    !hasPrevPage ? 'e-btn--disabled' : ''
                  }`}
                  onClick={handlePrevPage}
                  disabled={!hasPrevPage}
                >
                  上一頁
                </button>
                <span className="l-bird-survey__page-status">
                  第 {currentPage} / {totalPages} 頁
                </span>
                <button
                  type="button"
                  className={`e-btn e-btn--muted ${
                    !hasNextPage ? 'e-btn--disabled' : ''
                  }`}
                  onClick={handleNextPage}
                  disabled={!hasNextPage}
                >
                  下一頁
                </button>
              </div>
            </div>
            <div className="l-bird-survey__table-wrapper">
              <table className="l-bird-survey__table">
                <thead>
                  <tr>
                    {BIRD_SURVEY_COLUMNS.map((column) => (
                      <th key={column.key}>{column.title}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {isLoading && (
                    <tr>
                      <td colSpan={BIRD_SURVEY_COLUMNS.length}>
                        資料載入中...
                      </td>
                    </tr>
                  )}
                  {!isLoading && errorMessage && (
                    <tr>
                      <td colSpan={BIRD_SURVEY_COLUMNS.length}>
                        {errorMessage}
                      </td>
                    </tr>
                  )}
                  {!isLoading &&
                    !errorMessage &&
                    birdSurveyRecords.length === 0 && (
                      <tr>
                        <td colSpan={BIRD_SURVEY_COLUMNS.length}>
                          目前尚無資料。
                        </td>
                      </tr>
                    )}
                  {!isLoading &&
                    !errorMessage &&
                    birdSurveyRecords.map((record) => (
                      <tr key={record.id || record.occurrenceID}>
                        {BIRD_SURVEY_COLUMNS.map((column) => (
                          <td key={column.key}>
                            {formatTableValue(record, column)}
                          </td>
                        ))}
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </article>
    </>
  );
};

export default Content;
